import React from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import * as anno from 'anno'
import axios from 'axios'
import constants from '../../../lib/constants'
import DiffView from './DiffView'
import TableView from './TableView'
import ThumbnailView from './ThumbnailView'
import './ImageView.scss'

const THUMBNAIL_VIEW = 'Thumbnail'
const FULL_VIEW = 'Full'
const DIFF_VIEW = 'Diff'
const TABLE_VIEW = 'Table'
const LYMPHOCYTE_LABEL = 'wbc_m_l'
const NEUTROPHIL_LABEL = 'wbc_p_n'

const ignoreCodeMap = {
  0: 'Invalid Specimen',
  1: 'Outlier Count Detected',
  2: 'Outlier Removed by Cluster Preprocessing',
  3: 'Outlier Removed During Autocheck Ignored',
  4: 'Outlier Removed. Unable to detect cells to provide a reliable count',
  5: 'Rejecting all images in this job because there are not enough usable images',
  6: 'Outlier Removed. The count is too far from the median',
  7: 'Outlier Removed. Bubble detected'
}

// Required by Anno, do not remove.
// eslint-disable-next-line no-unused-vars
let scaledWidth = 1224
// eslint-disable-next-line no-unused-vars
let scaledHeight = 918

let autoCheckJobs = [
  constants.STRIP_TYPES.WBC_COUNTROL_AUTO_CHECK_LOW,
  constants.STRIP_TYPES.WBC_COUNTROL_AUTO_CHECK_NORMAL,
  constants.STRIP_TYPES.WBC_COUNTROL_AUTO_CHECK_HIGH,
  constants.STRIP_TYPES.COMBINED_AUTO_CHECK_LOW,
  constants.STRIP_TYPES.COMBINED_AUTO_CHECK_NORMAL,
  constants.STRIP_TYPES.COMBINED_AUTO_CHECK_HIGH
]

const levenshteinDistance = (str1 = '', str2 = '') => {
  const track = Array(str2.length + 1)
    .fill(null)
    .map(() => Array(str1.length + 1).fill(null))
  for (let i = 0; i <= str1.length; i += 1) {
    track[0][i] = i
  }
  for (let j = 0; j <= str2.length; j += 1) {
    track[j][0] = j
  }
  for (let j = 1; j <= str2.length; j += 1) {
    for (let i = 1; i <= str1.length; i += 1) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1
      track[j][i] = Math.min(
        track[j][i - 1] + 1, // deletion
        track[j - 1][i] + 1, // insertion
        track[j - 1][i - 1] + indicator // substitution
      )
    }
  }
  return track[str2.length][str1.length]
}

class ImageView extends React.Component {
  static defaultProps = {
    job: {},
    params: {},
    isProvider: false,
    loadImagesForJob: _.noop,
    markImageLabeled: _.noop,
    markImageIgnored: _.noop,
  };

  static propTypes = {
    params: PropTypes.shape({}),
    job: PropTypes.shape({
      images: PropTypes.array,
    }),
    isProvider: PropTypes.bool,
    loadImagesForJob: PropTypes.func,
    markImageLabeled: PropTypes.func,
    markImageIgnored: PropTypes.func,
  };

  constructor (props) {
    super(props)
    this.state = {
      activeTab: THUMBNAIL_VIEW,
      width: 900,
      height: 1200,
      showFlaggedImages: false,
      showMarkedImages: false,
      showKeypointImages: true,
    }
    this.imagesLoaded = 0
  }

  componentDidMount () {
    const { loadImagesForJob, params } = this.props
    loadImagesForJob(params.jobId)

    anno.addHandler('onAnnotationCreated', (annotation) => {
      if (!annotation.text) {
        // Standard Annotations
        annotation.text = 'wbc'
        if (this.props.job.strip_type === 'platelet') {
          annotation.text = 'plt'
        }
      } else if (autoCheckJobs.includes(this.props.job.strip_type)) {
        // Annotations for AutoCheck Jobs
        let imageObj = this.getImageObject(annotation.src)
        if (imageObj) {
          // Ensure correct label is applied
          let distN = levenshteinDistance(annotation.text, NEUTROPHIL_LABEL)
          let distL = levenshteinDistance(annotation.text, LYMPHOCYTE_LABEL)
          let actualLabel = distL < distN ? LYMPHOCYTE_LABEL : NEUTROPHIL_LABEL
          // Find which keypoints to include in selection
          let trueKeypoints = imageObj.keypoints.true || []
          let geo = annotation.shapes[0].geometry
          let minX = geo.x * this.state.width
          let maxX = (geo.x + geo.width) * this.state.width
          let minY = geo.y * this.state.height
          let maxY = (geo.y + geo.height) * this.state.height
          // Select those keypoints & update their labels in backend
          trueKeypoints
            .filter(
              (point) =>
                point.x > minX &&
                point.x < maxX &&
                point.y > minY &&
                point.y < maxY
            )
            .forEach((selectedPoint) => {
              selectedPoint.note = actualLabel
            })
          this.pushKeypoints(annotation.src, trueKeypoints, params.jobId)
          // Update corresponding annotations to show correct text
          anno
            .getAnnotations()
            .filter((a) => a.src === annotation.src)
            .filter(
              (a) =>
                a.shapes[0].geometry.x > geo.x &&
                a.shapes[0].geometry.x < geo.x + geo.width &&
                a.shapes[0].geometry.y > geo.y &&
                a.shapes[0].geometry.y < geo.y + geo.height
            )
            .forEach((a) => {
              a.text = actualLabel
            })
        }
        // Finally, remove the dragged box for cleanliness
        anno.removeAnnotation(annotation)
        return
      }
      var keypoint = this.getKeypointFromAnnotation(annotation)
      this.addKeypoint(annotation.src, keypoint, params.jobId)
    })

    anno.addHandler('onAnnotationRemoved', (annotation) => {
      var keypoint = this.getKeypointFromAnnotation(annotation)
      this.removeKeypoint(annotation.src, keypoint, params.jobId)
    })

    anno.addHandler('onAnnotationUpdated', (annotation) => {
      var keypoint = this.getKeypointFromAnnotation(annotation)
      this.updateKeypoint(
        annotation.src,
        keypoint,
        annotation.text,
        params.jobId
      )
    })

    let handlers = []
    let dragged = false

    document.addEventListener('mousedown', function () {
      dragged = false
    })
    document.addEventListener('mousemove', function () {
      dragged = true
    })

    const mouseUpHandler = (annotation, imgName) => {
      document.removeEventListener('mouseup', handlers.pop())
      if (dragged === true) {
        // Bulk edit keypoint labels
        // This case is handled by `onAnnotationCreated
      } else {
        // Single keypoint creation
        anno.addAnnotation(annotation)
        const keypoint = this.getKeypointFromAnnotation(annotation)
        this.addKeypoint(imgName, keypoint, params.jobId)
      }
    }

    anno.addHandler('onSelectionStarted', (event1, event2) => {
      const { params } = this.props
      const imgName = event2.currentTarget.previousSibling.id
      const imgElement = document.getElementById(imgName)
      const width = imgElement.clientWidth
      const height = imgElement.clientHeight
      const x = event1.offsetX / width
      const y = event1.offsetY / height
      let keypointWidth = 0.01
      let keypointHeight = (0.01 * 4) / 3

      let text = 'wbc'
      if (this.props.job.strip_type === 'platelet') {
        text = 'plt'
      } else if (autoCheckJobs.includes(this.props.job.strip_type)) {
        text = NEUTROPHIL_LABEL
        keypointWidth = 0.015
        keypointHeight = (0.015 * 4) / 3
      }
      const annotation = {
        src: imgName,
        text: text,
        shapes: [
          {
            type: 'rect',
            /** The shape geometry (relative coordinates) **/
            geometry: {
              x: x - keypointWidth / 2,
              y: y - keypointHeight / 2,
              width: keypointWidth,
              height: keypointHeight,
            },
          },
        ],
      }
      const handler = mouseUpHandler.bind(this, annotation, imgName)
      handlers.push(handler)
      document.addEventListener('mouseup', handler)
    })
  }

  convertGCSUrl = (originalUrl) => {
    const accessIdString = '?GoogleAccessId'
    const gcsUrl = 'https://storage.googleapis.com/'
    if (originalUrl.indexOf(accessIdString) > 0) {
      const urlWithoutAccessId = originalUrl.split(accessIdString)[0]
      const bucketPath = urlWithoutAccessId.split(gcsUrl)[1]
      const basePath = 'https://storage.googleapis.com'
      return `${basePath}/${bucketPath}`
    }
    return originalUrl
  };

  handleImageLoaded = () => {
    this.imagesLoaded += 1
    console.log('IMAGES LOADED', this.imagesLoaded)
    if (this.imagesLoaded === _.size(this.props.job.images)) {
      anno.setProperties({
        outline: 'green',
        outline_width: 0.2,
      })
      setTimeout(() => {
        anno.reset()
        this.createAnnotations(this.props.job.images)
      }, 1000)
    }
  };

  createAnnotations = (imageObjects) => {
    const imageElement = document.getElementById(imageObjects[0].raw)
    const globalHeight = imageElement.naturalHeight
    const globalWidth = imageElement.naturalWidth

    scaledWidth = imageElement.width
    scaledHeight = imageElement.height

    this.setState({
      height: globalHeight,
      width: globalWidth,
    })

    _.forEach(imageObjects, (imageObj) => {
      if (imageObj.keypoints) {
        const trueKeypoints = imageObj.keypoints.true || []
        // remove keypoints that occur twice
        const filteredKeypoints = _.filter(trueKeypoints, (keypoint, index) => {
          let foundIndex = trueKeypoints.findIndex(
            (otherKeypoint) =>
              JSON.stringify(otherKeypoint) === JSON.stringify(keypoint)
          )
          return foundIndex === index
        })
        imageObj.keypoints.true = filteredKeypoints
        _.forEach(filteredKeypoints, (keypoint) => {
          const height = keypoint.height
            ? keypoint.height / globalHeight
            : 16 / globalHeight
          const width = keypoint.width
            ? keypoint.width / globalWidth
            : 16 / globalWidth
          const x = Number((keypoint.x / globalWidth - width / 2).toFixed(6))
          const y = Number((keypoint.y / globalHeight - height / 2).toFixed(6))

          anno.addAnnotation({
            src: imageObj.raw,
            text: keypoint.note || 'wbc',
            shapes: [
              {
                type: 'rect',
                /** The shape geometry (relative coordinates) **/
                geometry: {
                  x: x,
                  y: y,
                  width: width,
                  height: height,
                },
              },
            ],
          })
        })
      }
    })
  };

  getKeypointFromAnnotation = (annotation) => {
    if (annotation) {
      const geometry = annotation.shapes[0].geometry
      const xCenter = geometry.x + geometry.width / 2.0
      const yCenter = geometry.y + geometry.height / 2.0
      const xCenterInPixels = Number((xCenter * this.state.width).toFixed(6))
      const yCenterInPixels = Number((yCenter * this.state.height).toFixed(6))

      return {
        x: xCenterInPixels,
        y: yCenterInPixels,
        height: geometry.height * this.state.height,
        width: geometry.width * this.state.width,
        note: annotation.text,
      }
    }
  };

  getImageObject = (imgName) => {
    const { job } = this.props
    return job.images.find((imageObj) => {
      return imageObj['marked'] === imgName || imageObj['raw'] === imgName
    })
  };

  pushKeypoints = (imgName, trueKeypoints, jobId) => {
    const correctedImgName = this.convertGCSUrl(imgName)
    return axios
      .post(`${constants.API_URL}/jobs/${jobId}/label_keypoints`, {
        image_name: correctedImgName.replace('marked', 'raw'),
        true_keypoints: trueKeypoints,
      })
      .then((response) => {
        console.log('Keypoint successfully updated', response.data)
      })
      .catch((err) => {
        console.error('Failed to update keypoint', err)
      })
  };

  addKeypoint = (imgName, keypoint, jobId) => {
    const imageObj = this.getImageObject(imgName)
    if (imageObj && keypoint) {
      const trueKeypoints = imageObj.keypoints.true || []
      trueKeypoints.push(keypoint)
      this.pushKeypoints(imgName, trueKeypoints, jobId)
    }
  };

  updateKeypoint = (imgName, keypoint, newNote, jobId) => {
    const imageObj = this.getImageObject(imgName)
    const index = this.findKeypoint(imgName, keypoint)
    if (index > -1 && newNote) {
      const trueKeypoints = imageObj.keypoints.true || []
      trueKeypoints[index].note = newNote
      return this.pushKeypoints(imgName, trueKeypoints, jobId)
    }
  };

  findKeypoint = (imgName, keypoint) => {
    const imageObj = this.getImageObject(imgName)
    const trueKeypoints = imageObj.keypoints.true || []
    const index = trueKeypoints.findIndex((key) => {
      return (
        Math.round(key.x) === Math.round(keypoint.x) &&
        Math.round(key.y) === Math.round(keypoint.y)
      )
    })
    return index
  };

  removeKeypoint = (imgName, keypoint, jobId) => {
    const index = this.findKeypoint(imgName, keypoint)
    const imageObj = this.getImageObject(imgName)
    if (index > -1) {
      const trueKeypoints = imageObj.keypoints.true || []
      trueKeypoints.splice(index, 1)
      this.pushKeypoints(imgName, trueKeypoints, jobId)
    }
  };

  markLabeled = (imgName, isLabeled) => {
    const { markImageLabeled, params } = this.props
    const correctedImgName = this.convertGCSUrl(imgName)
    markImageLabeled(params.jobId, correctedImgName, isLabeled)
  };

  markIgnored = (imgName, isIgnored) => {
    const { markImageIgnored, params } = this.props
    const correctedImgName = this.convertGCSUrl(imgName)
    markImageIgnored(params.jobId, correctedImgName, isIgnored)
  };

  unlabelAll = (imgName) => {
    const { params } = this.props
    return this.pushKeypoints(imgName, [], params.jobId).then(() => {
      anno.removeAll(imgName)
    })
  };

  setTab = (tabName) => {
    this.setState({
      activeTab: tabName,
    })
  };

  render () {
    const { job, params, isProvider } = this.props
    const { showMarkedImages, showFlaggedImages, showKeypointImages } =
      this.state
    const imageRows = []
    const onImgLoaded = this.handleImageLoaded
    _.forEach(job.images, (image, index) => {
      imageRows.push(
        <tr key={image.markedUrl + 'heading'}>
          <td colSpan={2}>
            <h1>{index}</h1>
            <br />
            <div>
              {job.region_data
                ? `Ignored Regions: ${_.filter(
                    _.keys(job.region_data[index]),
                    (key) => job.region_data[index][key]['ignored']
                  )}`
                : null}
            </div>
            <span style={{ fontSize: 14 }}>
              {image.markedUrl
                ? image.markedUrl.split('?GoogleAccessId')[0]
                : image.rawUrl.split('?GoogleAccessId')[0]}
            </span>
            <div style={{ marginTop: 20 }}>
              <button
                className={`btn ${
                  image.is_ignored ? 'btn-secondary' : 'btn-outline-secondary'
                }`}
                onClick={() =>
                  this.markIgnored(image.markedUrl, !image.is_ignored)
                }
              >
                Ignore
              </button>
              <button
                className={`btn ${
                  image.is_labeled ? 'btn-secondary' : 'btn-outline-secondary'
                } btn-labeled`}
                onClick={() =>
                  this.markLabeled(image.markedUrl, !image.is_labeled)
                }
              >
                Mark Labeled
              </button>
              {image.is_ignored ? (
                <div style={{ marginTop: 20 }}>
                  Ignore Code:{' '}
                  {ignoreCodeMap[image.ignore_code] || 'Image not ignored'}
                </div>
              ) : null}
            </div>
          </td>
        </tr>
      )

      if (showKeypointImages) {
        imageRows.push(
          <tr key={image.rawUrl}>
            <td>
              <img
                id={image.rawUrl}
                src={image.rawUrl}
                // id={'https://assets.fishersci.com/TFS-Assets/CCG/product-images/F73633~p.eps-650.jpg'}
                // src={'https://assets.fishersci.com/TFS-Assets/CCG/product-images/F73633~p.eps-650.jpg'}
                className='annotatable'
                style={{ width: '100%' }}
                onLoad={onImgLoaded}
              />
            </td>
          </tr>
        )
      }
      if (showMarkedImages) {
        imageRows.push(
          <tr key={image.markedUrl}>
            <td>
              <div>
                <img
                  id={image.markedUrl}
                  src={image.markedUrl}
                  style={{ width: '100%' }}
                />
              </div>
            </td>
          </tr>
        )
      }

      if (showFlaggedImages) {
        imageRows.push(
          <tr key={image.flaggedUrl}>
            <td>
              <div>
                <img
                  id={image.flaggedUrl}
                  src={image.flaggedUrl}
                  style={{ width: '100%' }}
                />
              </div>
            </td>
          </tr>
        )
      }
    })

    const { activeTab, width } = this.state

    let tabBody = null
    if (activeTab === THUMBNAIL_VIEW) {
      tabBody = (
        <div>
          <ThumbnailView imageObjects={job.images} />
        </div>
      )
    } else if (activeTab === FULL_VIEW) {
      tabBody = (
        <div>
          <div style={{ margin: 20 }}>
            <button
              className={`btn ${
                showKeypointImages ? 'btn-secondary' : 'btn-outline-secondary'
              } btn-labeled`}
              onClick={() =>
                this.setState({
                  showKeypointImages: !this.state.showKeypointImages,
                })
              }
            >
              Show Keypoint Images
            </button>
            <button
              className={`btn ${
                showMarkedImages ? 'btn-secondary' : 'btn-outline-secondary'
              } btn-labeled`}
              onClick={() =>
                this.setState({
                  showMarkedImages: !this.state.showMarkedImages,
                })
              }
            >
              Show Marked Images
            </button>
            <button
              className={`btn ${
                showFlaggedImages ? 'btn-secondary' : 'btn-outline-secondary'
              } btn-labeled`}
              onClick={() =>
                this.setState({
                  showFlaggedImages: !this.state.showFlaggedImages,
                })
              }
            >
              Show Flagging Images
            </button>
          </div>
          <table className='table table-striped'>
            <thead className='thead-dark' />
            <tbody>{imageRows}</tbody>
          </table>
        </div>
      )
    } else if (activeTab === DIFF_VIEW) {
      tabBody = (
        <div>
          <DiffView
            imageObjects={job.images}
            width={width}
            onUpdateKeypoint={(image, keypoint, label) =>
              this.updateKeypoint(image, keypoint, label, params.jobId)
            }
          />
        </div>
      )
    } else {
      tabBody = <TableView job={job} />
    }

    return (
      <div>
        {isProvider ? (
          <div />
        ) : (
          <div>
            <ul className='nav nav-tabs' style={{ marginBottom: '15px' }}>
              <li className='nav-item'>
                <a
                  className={`clickable nav-link ${
                    activeTab === THUMBNAIL_VIEW ? 'active' : ''
                  }`}
                  onClick={() => this.setTab(THUMBNAIL_VIEW)}
                >
                  Thumbnail View
                </a>
              </li>
              <li className='nav-item'>
                <a
                  className={`clickable nav-link ${
                    activeTab === FULL_VIEW ? 'active' : ''
                  }`}
                  onClick={() => this.setTab(FULL_VIEW)}
                >
                  Full View
                </a>
              </li>
              {width ? (
                <li className='nav-item'>
                  <a
                    className={`clickable nav-link ${
                      activeTab === DIFF_VIEW ? 'active' : ''
                    }`}
                    onClick={() => this.setTab(DIFF_VIEW)}
                  >
                    Diff View
                  </a>
                </li>
              ) : null}
              <li className='nav-item'>
                <a
                  className={`clickable nav-link ${
                    activeTab === TABLE_VIEW ? 'active' : ''
                  }`}
                  onClick={() => this.setTab(TABLE_VIEW)}
                >
                  Table View
                </a>
              </li>
            </ul>
            {tabBody}
          </div>
        )}
      </div>
    )
  }
}

export default ImageView



// WEBPACK FOOTER //
// ./src/routes/Images/components/ImageView.js